import {
  HiDocumentText,
  HiGlobeAlt,
  HiCode,
  HiPencilAlt,
  HiCheckCircle,
  HiClipboardList,
  HiAcademicCap,
  HiUserGroup,
  HiLightBulb,
  HiDocumentDuplicate,
  HiPresentationChartBar,
  HiChat,
  HiPaperAirplane,
  HiCurrencyPound,
  HiCalendar,
  HiSupport,
} from 'react-icons/hi';

const servicesData = [
  {
    slug: 'cv-writing',
    icon: HiDocumentText,
    title: 'CV Writing Services',
    tagline: 'Your story, professionally told.',
    color: 'from-pink-50 to-rose-50',
    iconBg: 'bg-pink-100',
    iconColor: 'text-pink-600',
    accentColor: 'pink',
    heroImage: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&h=600&fit=crop',
    heroDescription:
      'A powerful CV is your first impression — make it count. I craft tailored, professional CVs that highlight your strengths, experience, and potential, helping you stand out in competitive job markets across Bangladesh and the UK.',
    features: [
      'Bangladesh-style CV writing',
      'UK-standard CV formatting',
      'ATS-optimised layouts',
      'Cover letter support',
    ],
    details: [
      {
        icon: HiClipboardList,
        title: 'Bangladesh-Style CV Writing',
        description:
          'Tailored CVs designed for the Bangladeshi job market. I understand local employer expectations, formatting preferences, and the cultural nuances that make your application resonate with recruiters in Bangladesh. Whether you\'re applying to corporate firms, NGOs, or government positions, your CV will be perfectly crafted.',
      },
      {
        icon: HiAcademicCap,
        title: 'UK-Standard CV Writing',
        description:
          'Professional CVs formatted to UK standards. I ensure your CV follows best practices used by top UK recruiters — concise, skills-focused, and compliant with Applicant Tracking Systems (ATS). Perfect for graduates entering the UK job market or professionals looking to advance.',
      },
      {
        icon: HiCheckCircle,
        title: 'ATS-Optimised Layouts',
        description:
          'Many companies use Applicant Tracking Systems to filter CVs before a human ever sees them. I design ATS-friendly layouts with the right keywords, formatting, and structure to ensure your CV passes automated screening and reaches the hiring manager.',
      },
      {
        icon: HiDocumentDuplicate,
        title: 'Cover Letter Support',
        description:
          'A great cover letter complements your CV and tells your personal story. I write compelling, personalised cover letters that explain your motivation, highlight your key achievements, and make employers want to meet you.',
      },
    ],
    process: [
      { step: '01', title: 'Consultation', description: 'We discuss your career goals, experience, and target roles.' },
      { step: '02', title: 'Draft Creation', description: 'I craft your CV with professional formatting and compelling content.' },
      { step: '03', title: 'Review & Refine', description: 'You review the draft and I make revisions until it\'s perfect.' },
      { step: '04', title: 'Final Delivery', description: 'You receive your polished CV in PDF and editable formats.' },
    ],
    pricing: [
      { plan: 'Basic', price: '£20', features: ['Single CV format', 'One revision round', '3-day delivery', 'PDF format'] },
      { plan: 'Middle', price: '£50', features: ['UK + BD formats', 'Unlimited revisions', '2-day delivery', 'PDF + DOCX', 'Cover letter included'], popular: true },
      { plan: 'Custom', price: 'Custom', features: ['All formats', 'Unlimited revisions', '24-hour delivery', 'All file formats', 'Cover letter', 'LinkedIn optimisation'] },
    ],
    faq: [
      { question: 'How long does it take to receive my CV?', answer: 'Depending on your chosen plan, delivery ranges from 24 hours to 3 business days. Rush delivery is available on request.' },
      { question: 'Can you write CVs for any industry?', answer: 'Yes! I have experience writing CVs for IT, finance, healthcare, education, engineering, marketing, and many more sectors.' },
      { question: 'Do you offer revisions?', answer: 'Absolutely. The Basic plan includes one revision round, while Professional and Premium plans include unlimited revisions until you\'re completely satisfied.' },
      { question: 'What information do you need from me?', answer: 'I\'ll need your current CV (if available), education history, work experience, skills, and the type of roles you\'re targeting. I provide a detailed questionnaire to make the process easy.' },
    ],
  },
  {
    slug: 'student-support',
    icon: HiGlobeAlt,
    title: 'Student support',
    tagline: 'Navigate the UK visa process with confidence.',
    color: 'from-purple-50 to-violet-50',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    accentColor: 'purple',
    heroImage: '/images/student-support.webp',
    heroDescription:
      'Studying in the UK is an exciting journey, and I\'m here to support you every step of the way. From mastering your IELTS speaking skills to crafting professional CVs and cover letters, I provide comprehensive student support services. Whether you need help with assignments, guidance on your graduation visa application, or personalized one-to-one IELTS coaching, I offer tailored assistance to help you succeed academically and professionally in the UK.',
    features: [
      'Assignment support',
      'IELTS specking ONE TO ONE support',
      'Cv writing',
      'Cover letter writing',
      'Graduation visa application support',
    ],
    details: [
      {
        icon: HiAcademicCap,
        title: 'Student Visa Guidance',
        description:
          'Comprehensive support for Tier 4 / Student visa applications. I help you understand eligibility requirements, gather necessary documentation, prepare financial evidence, and submit a strong application. I\'ve helped hundreds of students successfully obtain their UK student visas.',
      },
      {
        icon: HiLightBulb,
        title: 'Post-Graduation Support',
        description:
          'Graduating is just the beginning. I provide guidance on the Graduate Route visa, Skilled Worker visa pathways, and how to transition from student life to your professional career in the UK. I help you understand your options and plan your next steps.',
      },
      {
        icon: HiClipboardList,
        title: 'Step-by-Step Assistance',
        description:
          'No jargon, no confusion. I break down the entire visa process into manageable steps, guiding you through each stage — from choosing the right visa category to preparing for biometrics appointments. You\'ll always know exactly what comes next.',
      },
      {
        icon: HiDocumentDuplicate,
        title: 'Document Preparation',
        description:
          'Proper documentation is crucial for visa success. I help you prepare, organise, and review all required documents including financial statements, academic transcripts, references, and supporting letters to ensure nothing is missed.',
      },
    ],
    process: [
      { step: '01', title: 'Initial Assessment', description: 'We evaluate your visa eligibility and determine the best route for you.' },
      { step: '02', title: 'Document Checklist', description: 'I provide a detailed checklist of all required documents for your application.' },
      { step: '03', title: 'Application Support', description: 'I guide you through filling out the application forms correctly.' },
      { step: '04', title: 'Review & Submit', description: 'Final review of your complete application before submission.' },
    ],
    pricing: [
      { plan: 'Basic', price: '£20', features: ['30-min video call', 'Eligibility assessment', 'Route recommendation', 'Basic guidance'] },
      { plan: 'Middle', price: '£35', features: ['Full application support', 'Document checklist', 'Form guidance', 'Email support', 'One review round'], popular: true },
      { plan: 'Custom', price: 'Custom', features: ['Everything in Standard', 'Document preparation', 'Unlimited reviews', 'Priority support', 'Post-decision guidance'] },
    ],
    faq: [
      { question: 'Can you guarantee my visa will be approved?', answer: 'No legitimate advisor can guarantee visa approval as the final decision rests with UKVI. However, I ensure your application is as strong as possible by meeting all requirements and presenting your case professionally.' },
      { question: 'Which visa types do you support?', answer: 'I primarily support Student visas, Graduate Route visas, and Skilled Worker visa applications. For other visa categories, please contact me for a consultation.' },
      { question: 'How early should I start the visa process?', answer: 'I recommend starting at least 3-4 months before your intended travel date. This allows ample time for document preparation, application submission, and processing.' },
      { question: 'Do you provide immigration legal advice?', answer: 'I provide guidance and support with the application process. For complex legal matters, I can refer you to a registered OISC advisor or immigration solicitor.' },
    ],
  },
  {
    slug: 'software-collaboration',
    icon: HiCode,
    title: 'Software Service Collaboration',
    tagline: 'Building digital solutions, together.',
    color: 'from-blue-50 to-indigo-50',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    accentColor: 'blue',
    heroImage: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop',
    heroDescription:
      'I believe in the power of collaboration. Whether you need a website, a web application, or a digital tool, I work closely with clients and development teams to deliver software solutions that are practical, elegant, and built to last.',
    features: [
      'Client collaboration',
      'Project partnerships',
      'Digital solutions',
      'Teamwork-driven approach',
    ],
    details: [
      {
        icon: HiUserGroup,
        title: 'Client Collaboration',
        description:
          'I work hand-in-hand with you to understand your vision, requirements, and goals. Through regular communication, feedback loops, and transparent processes, we build solutions that truly serve your needs. Your input drives every decision.',
      },
      {
        icon: HiPresentationChartBar,
        title: 'Project Partnerships',
        description:
          'Open to strategic partnerships with businesses, startups, and fellow professionals. Whether you need a technical partner for a new venture or support on an existing project, I bring dedication, skill, and a collaborative spirit to every partnership.',
      },
      {
        icon: HiCode,
        title: 'Digital Solutions',
        description:
          'From responsive websites and web applications to custom digital tools, I help bring your ideas to life with modern technologies. Every solution is designed with user experience, performance, and scalability in mind.',
      },
      {
        icon: HiChat,
        title: 'Teamwork-Driven Approach',
        description:
          'Great software is built by great teams. I integrate seamlessly into your workflow, whether you\'re using Agile, Scrum, or your own methodology. Clear communication and shared goals are the foundation of every project.',
      },
    ],
    process: [
      { step: '01', title: 'Discovery', description: 'We discuss your project vision, goals, and technical requirements.' },
      { step: '02', title: 'Planning', description: 'I create a detailed project plan with timelines and milestones.' },
      { step: '03', title: 'Development', description: 'Collaborative development with regular updates and feedback sessions.' },
      { step: '04', title: 'Launch & Support', description: 'Project delivery with post-launch support and maintenance.' },
    ],
    pricing: [
      { plan: 'Starter', price: '£150', features: ['Landing page', 'Responsive design', 'Basic SEO', '1 revision round', '7-day delivery'] },
      { plan: 'Business', price: '£300', features: ['Multi-page website', 'Custom design', 'SEO optimised', 'CMS integration', 'Unlimited revisions'], popular: true },
      { plan: 'Enterprise', price: 'Custom', features: ['Full web application', 'Custom features', 'API integration', 'Ongoing support', 'Priority development'] },
    ],
    faq: [
      { question: 'What technologies do you work with?', answer: 'I work with modern web technologies including React, Node.js, Tailwind CSS, WordPress, and more. The technology stack is chosen based on your project requirements.' },
      { question: 'Can you work with my existing development team?', answer: 'Absolutely! I\'m experienced in collaborative workflows and can integrate into your existing team using your preferred tools and methodologies.' },
      { question: 'How long does a typical project take?', answer: 'Timelines vary based on project scope. A simple landing page may take a week, while a full web application could take several weeks to months. I provide detailed timelines during the planning phase.' },
      { question: 'Do you offer ongoing maintenance?', answer: 'Yes! I offer post-launch maintenance and support packages to keep your project running smoothly and up to date.' },
    ],
  },
  {
    slug: 'content-writing',
    icon: HiPencilAlt,
    title: 'Content Writing',
    tagline: 'Words that work for your business.',
    color: 'from-emerald-50 to-teal-50',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    accentColor: 'emerald',
    heroImage: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800&h=600&fit=crop',
    heroDescription:
      'Great content tells your story, engages your audience, and drives results. I provide professional content writing services for blogs, business documents, and marketing materials — all crafted with clarity, purpose, and your unique voice in mind.',
    features: [
      'Blog writing',
      'Business documents',
      'Professional content',
      'SEO-friendly writing',
    ],
    details: [
      {
        icon: HiPencilAlt,
        title: 'Blog Writing',
        description:
          'Engaging, well-researched blog posts that attract readers and establish your authority. Whether you need weekly articles, thought leadership pieces, or industry insights, I create content that keeps your audience coming back for more.',
      },
      {
        icon: HiDocumentDuplicate,
        title: 'Business Documents',
        description:
          'Professional business writing including reports, proposals, presentations, and corporate communications. I ensure every document reflects your brand\'s professionalism and communicates your message with clarity and impact.',
      },
      {
        icon: HiPresentationChartBar,
        title: 'Marketing Content',
        description:
          'Compelling marketing copy for websites, brochures, email campaigns, and social media. I write content that resonates with your target audience, communicates your value proposition, and drives conversions.',
      },
      {
        icon: HiLightBulb,
        title: 'SEO-Friendly Writing',
        description:
          'Content optimised for search engines without sacrificing readability. I research relevant keywords, structure content for SEO best practices, and create articles that rank well and provide genuine value to readers.',
      },
    ],
    process: [
      { step: '01', title: 'Brief & Research', description: 'We discuss your content needs, audience, tone, and goals.' },
      { step: '02', title: 'Outline & Approval', description: 'I create a content outline for your review before writing begins.' },
      { step: '03', title: 'Writing & Editing', description: 'Professional writing with thorough editing and proofreading.' },
      { step: '04', title: 'Delivery & Revisions', description: 'Final content delivered with revisions until you\'re satisfied.' },
    ],
    pricing: [
      { plan: 'Single Article', price: '£20', features: ['Up to 1000 words', 'SEO optimised', 'One revision', '3-day delivery'] },
      { plan: 'Monthly Package', price: '£199', features: ['4 articles/month', 'Up to 1500 words each', 'SEO optimised', 'Unlimited revisions', 'Content calendar'], popular: true },
      { plan: 'Business Package', price: '£399', features: ['8 articles/month', 'Up to 2000 words each', 'Full SEO strategy', 'Unlimited revisions', 'Content calendar', 'Social media snippets'] },
    ],
    faq: [
      { question: 'What topics can you write about?', answer: 'I write across a wide range of topics including technology, business, career development, education, lifestyle, and more. For highly technical subjects, I conduct thorough research and may consult with subject matter experts.' },
      { question: 'Do you write in British or American English?', answer: 'I can write in both! By default I use British English given my UK base, but I\'m happy to adapt to American English or any other style guide you require.' },
      { question: 'Can you match our brand voice?', answer: 'Absolutely. I take time to understand your brand\'s tone, style, and values. Whether you need formal, conversational, technical, or creative writing, I adapt my style to match your brand perfectly.' },
      { question: 'What if I\'m not happy with the content?', answer: 'Your satisfaction is my priority. All plans include revisions, and I work closely with you until the content meets your expectations. I also offer a clear feedback process to ensure efficient collaboration.' },
    ],
  },
  {
    slug: 'flight-tickets',
    icon: HiPaperAirplane,
    title: 'International Flight Ticket Purchasing',
    tagline: 'Best deals on international flights, hassle-free.',
    color: 'from-orange-50 to-amber-50',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    accentColor: 'orange',
    heroImage: '/images/flight-tickets.jpeg',
    heroDescription:
      'Finding the right international flight at the best price can be stressful and time-consuming. I take the hassle out of flight booking by sourcing the best deals, managing your reservations, and ensuring a smooth travel experience from start to finish.',
    features: [
      'Best price guarantee',
      'Multi-airline comparison',
      'Flexible booking options',
      '24/7 booking support',
    ],
    details: [
      {
        icon: HiCurrencyPound,
        title: 'Best Price Guarantee',
        description:
          'I compare fares across multiple airlines and booking platforms to find you the most competitive prices. Whether you\'re flying economy or business class, I ensure you get the best value for your money on every international route.',
      },
      {
        icon: HiGlobeAlt,
        title: 'Multi-Airline Comparison',
        description:
          'With access to a wide network of airlines and travel partners, I compare options across carriers to find the ideal flight for your schedule, budget, and preferences. From direct flights to the best connecting routes, every option is explored.',
      },
      {
        icon: HiCalendar,
        title: 'Flexible Booking Options',
        description:
          'Plans change — I understand that. I help you find flights with flexible booking policies, assist with date changes, and manage cancellations or rebookings so you always have peace of mind when travelling internationally.',
      },
      {
        icon: HiSupport,
        title: '24/7 Booking Support',
        description:
          'From the moment you enquire to the moment you board, I\'m here to help. Whether you need last-minute changes, special meal requests, extra baggage, or transit visa guidance, I provide continuous support throughout your journey.',
      },
    ],
    process: [
      { step: '01', title: 'Travel Enquiry', description: 'Share your travel dates, destination, and preferences with me.' },
      { step: '02', title: 'Flight Search', description: 'I search across airlines and platforms for the best options and prices.' },
      { step: '03', title: 'Review & Confirm', description: 'You review the shortlisted flights and choose your preferred option.' },
      { step: '04', title: 'Booking & E-Ticket', description: 'I complete the booking and deliver your confirmed e-ticket.' },
    ],
    pricing: [
      { plan: 'Economy', price: 'From £15', features: ['Single flight booking', 'Fare comparison', 'E-ticket delivery', 'Email support'] },
      { plan: 'Premium', price: 'From £30', features: ['Return flight booking', 'Multi-airline search', 'Flexible options', 'Rebooking assistance', 'Priority support'], popular: true },
      { plan: 'Group / Corporate', price: 'Custom', features: ['Group bookings (5+)', 'Corporate travel rates', 'Multi-city itineraries', 'Dedicated agent', 'Invoice billing'] },
    ],
    faq: [
      { question: 'Which destinations can you book flights to?', answer: 'I can book international flights to virtually any destination worldwide. Popular routes include UK to Bangladesh, India, Middle East, Europe, and North America, but I\'m happy to help with any international route.' },
      { question: 'How far in advance should I book?', answer: 'For the best prices, I recommend booking 4-8 weeks in advance for international flights. However, I can also find competitive last-minute deals when needed.' },
      { question: 'Can you help with transit visas?', answer: 'Yes! If your flight involves a layover in a country that requires a transit visa, I\'ll advise you on the requirements and help you prepare the necessary documentation.' },
      { question: 'Do you handle group bookings?', answer: 'Absolutely. I offer special rates and dedicated support for group bookings of 5 or more passengers, including family trips, corporate travel, and group tours.' },
    ],
  },
  {
    slug: 'small-business-support',
    icon: HiLightBulb,
    title: 'Small Business Support',
    tagline: 'Complete digital solutions for growing businesses.',
    color: 'from-indigo-50 to-blue-50',
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    accentColor: 'blue',
    heroImage: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=600&fit=crop',
    heroDescription:
      'Growing your small business requires the right digital tools and marketing support. I offer comprehensive services including mobile app development, website and webpage creation, marketing strategies, and professional design work for logos, menus, and posters. All services are customized to your specific requirements with flexible pricing that works for small businesses.',
    features: [
      'Mobile app development',
      'Website & webpage development',
      'Marketing support',
      'Logo, menu & poster design',
      'Requirement-based pricing',
    ],
    details: [
      {
        icon: HiCode,
        title: 'Mobile App & Web Development',
        description:
          'From simple landing pages to full-featured mobile applications, I develop digital solutions tailored to your business needs. Whether you need a mobile app for iOS/Android or a responsive website, I work with you to create user-friendly, professional platforms that help your business grow.',
      },
      {
        icon: HiPresentationChartBar,
        title: 'Marketing Support',
        description:
          'Effective marketing drives business growth. I provide marketing support including strategy development, social media planning, content creation, and campaign management. Whether you\'re launching a new product or building brand awareness, I help you reach your target audience.',
      },
      {
        icon: HiPencilAlt,
        title: 'Design Services',
        description:
          'Professional design creates lasting impressions. I offer logo design, menu creation for restaurants/cafes, poster design for events and promotions, and other graphic design services. All designs are custom-made to reflect your brand identity and business goals.',
      },
      {
        icon: HiCurrencyPound,
        title: 'Flexible Pricing',
        description:
          'Every small business has different needs and budgets. I offer requirement-based pricing with no fixed packages, allowing you to get exactly what you need without paying for services you don\'t. Free consultations help determine the best solution for your specific situation.',
      },
    ],
    process: [
      { step: '01', title: 'Free Consultation', description: 'We discuss your business needs, goals, and budget to understand your requirements.' },
      { step: '02', title: 'Custom Proposal', description: 'I provide a detailed proposal with pricing based on your specific requirements.' },
      { step: '03', title: 'Development & Design', description: 'Once approved, I begin work on your project with regular updates and feedback loops.' },
      { step: '04', title: 'Launch & Support', description: 'Your project is delivered with documentation and optional ongoing support.' },
    ],
    pricing: [
      { plan: 'Logo Design', price: 'Custom', features: ['Initial consultation', 'Multiple concept options', '3 revision rounds', 'Final files (PNG, SVG, AI)', 'Brand color palette'] },
      { plan: 'Website Development', price: 'Custom', features: ['Responsive design', 'Up to 10 pages', 'Mobile-friendly', 'SEO optimization', 'Content management system', '3 months support'], popular: true },
      { plan: 'Complete Package', price: 'Custom', features: ['Website or mobile app', 'Logo & branding design', 'Marketing strategy', 'Social media setup', 'Poster/menu design', '6 months support'] },
    ],
    faq: [
      { question: 'What does "requirement-based pricing" mean?', answer: 'Instead of fixed packages, I provide custom quotes based on your specific needs. After our free consultation, I\'ll propose pricing that reflects the scope, complexity, and timeline of your project. This ensures you only pay for what you actually need.' },
      { question: 'Do you offer mobile app development for both iOS and Android?', answer: 'Yes! I develop cross-platform mobile applications that work on both iOS and Android devices, as well as native apps if required for your specific use case.' },
      { question: 'Can you help with ongoing marketing after the initial project?', answer: 'Absolutely. I offer ongoing marketing support including social media management, content creation, and campaign optimization. We can arrange monthly retainers or project-based marketing work depending on your needs.' },
      { question: 'What if I only need one service like logo design?', answer: 'No problem! You can choose any individual service you need. There\'s no requirement to purchase a complete package. Whether you need just a logo, just a website, or just marketing support, I can help with exactly what you need.' },
    ],
  },
];

export default servicesData;
